import {
  type ApprovalChoice,
  type ConnectionState,
  type RunAction,
  type RunControlState,
  type RunStatus,
  type SessionRuntimeState,
} from "@herskin/contracts";
import { IconButton, StatusBadge } from "@herskin/ui";
import { IconX as X } from "@tabler/icons-react";
import { statusLabel } from "./presentation";
import { RunInspector } from "./RunInspector";
import { formatCompactTokens } from "./runtimePresentation";

interface ChatInspectorProps {
  visible: boolean;
  displayedRunStatus: RunStatus | null;
  runControl: RunControlState | null;
  runControlBusy: string | null;
  runControlError: string;
  connection: ConnectionState | null;
  runtimeState: SessionRuntimeState | null;
  contextLabel: string;
  contextCopy: string;
  onClose: () => void;
  onApproval: (action: Extract<RunAction, { type: "approval" }>, choice: ApprovalChoice) => void;
  onClarification: (action: Extract<RunAction, { type: "clarification" }>, answer: string) => void;
  onStop: () => void;
}

/** 会话右侧检查器；只呈现运行、连接和上下文事实，不持有业务状态。 */
export function ChatInspector({
  visible,
  displayedRunStatus,
  runControl,
  runControlBusy,
  runControlError,
  connection,
  runtimeState,
  contextLabel,
  contextCopy,
  onClose,
  onApproval,
  onClarification,
  onStop,
}: ChatInspectorProps) {
  if (!visible) return null;
  const contextUsage = runtimeState?.usage ?? null;

  return (
    <aside className="inspector">
      <div className="inspector-header">
        <div>
          <span className="section-kicker">当前运行</span>
          <h2>检查器</h2>
        </div>
        <IconButton aria-label="关闭检查器" onClick={onClose}>
          <X />
        </IconButton>
      </div>
      <section className="inspector-card">
        <span className="card-label">会话状态</span>
        <strong>{statusLabel(displayedRunStatus)}</strong>
        <p>
          {displayedRunStatus === "unknown"
            ? "发送结果需要核对；这不代表任务仍在执行，也不会自动重发。"
            : displayedRunStatus
              ? "任务继续在服务器运行，切换会话不会中断。"
              : "当前没有正在执行的任务。"}
        </p>
      </section>
      <RunInspector
        state={runControl}
        busyActionId={runControlBusy}
        error={runControlError}
        onApproval={onApproval}
        onClarification={onClarification}
        onStop={onStop}
      />
      <section className="inspector-card">
        <span className="card-label">连接路径</span>
        {connection?.layers.map((layer) => (
          <div className="connection-row" key={layer.layer}>
            <span>
              {layer.layer === "browser_to_herskin"
                ? "浏览器 → HerSkin"
                : layer.layer === "herskin_to_hermes"
                  ? "HerSkin → Hermes"
                  : "当前会话"}
            </span>
            <StatusBadge tone={layer.status === "online" ? "success" : "warning"} showDot>
              {layer.status === "online" ? "可用" : "恢复中"}
            </StatusBadge>
          </div>
        ))}
      </section>
      <section className="inspector-card">
        <span className="card-label">上下文用量</span>
        <strong>{contextLabel}</strong>
        {contextUsage?.occupancyMeasurement === "live_window" &&
        contextUsage.contextPercent !== null ? (
          <div
            className="context-meter"
            role="meter"
            aria-label="Hermes 当前上下文占用"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(contextUsage.contextPercent)}
          >
            <span style={{ width: `${contextUsage.contextPercent}%` }} />
          </div>
        ) : null}
        <p>{contextCopy}</p>
        {runtimeState?.model ? (
          <dl className="runtime-facts">
            <div>
              <dt>当前会话模型</dt>
              <dd>{runtimeState.model.model}</dd>
            </div>
            {runtimeState.model.provider ? (
              <div>
                <dt>Provider</dt>
                <dd>{runtimeState.model.provider}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
        {contextUsage ? (
          <dl className="runtime-facts runtime-facts--usage">
            <div>
              <dt>累计输入</dt>
              <dd>{formatCompactTokens(contextUsage.inputTokens)}</dd>
            </div>
            <div>
              <dt>累计输出</dt>
              <dd>{formatCompactTokens(contextUsage.outputTokens)}</dd>
            </div>
            <div>
              <dt>缓存读取</dt>
              <dd>{formatCompactTokens(contextUsage.cacheReadTokens)}</dd>
            </div>
            <div>
              <dt>API 调用</dt>
              <dd>{contextUsage.apiCalls.toLocaleString("zh-CN")}</dd>
            </div>
            {contextUsage.reasoningTokens > 0 ? (
              <div>
                <dt>推理 Token</dt>
                <dd>{formatCompactTokens(contextUsage.reasoningTokens)}</dd>
              </div>
            ) : null}
            {contextUsage.compressions !== null ? (
              <div>
                <dt>上下文压缩</dt>
                <dd>{contextUsage.compressions}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </section>
    </aside>
  );
}
